import React from "react";
import styled from "styled-components";
import { CheckCircleIcon } from "lucide-react";
import { useFluidForm, useFormInstance } from "./FormContext";
import { getPostSubmissionActionsFromConfig } from "../../../shared/forms/stylingForm";
import { useStyleConfig } from "./EmbeddedStyles";
import { useFormInstanceDispatch } from "./FormInstance.service";
const SuccessContainer = styled.div `
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;
const SuccessContent = styled.div `
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const SuccessCard = styled.div `
  text-align: center;
  padding: 2rem;
  max-width: 32rem;
`;
const IconWrapper = styled.div `
  margin: 0 auto 2rem;
  width: 5rem;
  height: 5rem;
  background: ${props => props.$bgColor || '#f0fdf4'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const ShareSection = styled.div `
  margin-bottom: 2rem;
`;
const ShareButtons = styled.div `
  display: flex;
  justify-content: center;
  gap: 1rem;
`;
const ShareButton = styled.button `
  width: 2.5rem;
  height: 2.5rem;
  background: ${props => props.$bgColor};
  color: white;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: ${props => props.$hoverColor};
  }
`;
const NewSubmissionLink = styled.button `
  background: none;
  border: none;
  color: ${props => props.$color || '#6b7280'};
  font-size: 0.875rem;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  padding: 0.5rem 0;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;
const BrandingSection = styled.div `
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
`;
export function FormSubmittedMessage({ form, title, message, buttonText, onButtonClick }) {
    const configService = useFluidForm();
    const styles = useStyleConfig();
    const instanceId = useFormInstance();
    const dispatch = useFormInstanceDispatch(instanceId);
    // Get post-submission configurations with defaults
    const actions = getPostSubmissionActionsFromConfig(configService);
    // Use config values or fallback to props or defaults
    const finalTitle = title || actions.thankYouMessage || "Form Submitted Successfully!";
    const finalMessage = message || "Your submission has been processed and saved.";
    const finalButtonText = buttonText || actions.ctaButtonText || "Continue";
    const finalButtonUrl = actions.ctaButtonUrl;
    // Handle redirect action type
    React.useEffect(() => {
        if (actions.action === 'redirect' && actions.redirectUrl) {
            // Add a small delay to show the success state briefly before redirecting
            const timer = setTimeout(() => {
                window.location.href = actions.redirectUrl;
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [actions.action, actions.redirectUrl]);
    const handleButtonClick = () => {
        if (onButtonClick) {
            onButtonClick();
        }
        else if (actions.ctaButtonEnabled && finalButtonUrl) {
            window.open(finalButtonUrl, '_blank');
        }
        else {
            // Default behavior - could redirect or close modal
            console.log('Form submission completed');
        }
    };
    return (React.createElement(SuccessContainer, { className: "f-bg" },
        React.createElement(SuccessContent, null,
            React.createElement(SuccessCard, null,
                React.createElement(IconWrapper, { "$bgColor": styles.backgroundColorLightHex },
                    React.createElement(CheckCircleIcon, { size: 48, color: styles.primaryColor })),
                React.createElement("h2", { className: "f-text", style: { fontSize: '1.875rem', fontWeight: 'bold', marginBottom: '1rem' } }, finalTitle),
                React.createElement("p", { className: "f-text-light", style: { marginBottom: '2rem', fontSize: '1.125rem' } }, finalMessage),
                actions.ctaButtonEnabled && (React.createElement("button", { className: "fluid-button-primary", onClick: handleButtonClick, style: { marginBottom: '2.5rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' } }, finalButtonText)),
                actions.showSocialShare && (React.createElement(ShareSection, null,
                    React.createElement("p", { className: "f-text-light", style: { fontSize: '0.875rem', marginBottom: '0.75rem' } }, "Share this form with your colleagues"),
                    React.createElement(ShareButtons, null,
                        React.createElement(ShareButton, { "$bgColor": "#1d4ed8", "$hoverColor": "#1e40af", onClick: () => window.open('https://facebook.com', '_blank') },
                            React.createElement("svg", { width: "20", height: "20", fill: "currentColor", viewBox: "0 0 24 24" },
                                React.createElement("path", { d: "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" }))),
                        React.createElement(ShareButton, { "$bgColor": "#0ea5e9", "$hoverColor": "#0284c7", onClick: () => window.open('https://twitter.com', '_blank') },
                            React.createElement("svg", { width: "20", height: "20", fill: "currentColor", viewBox: "0 0 24 24" },
                                React.createElement("path", { d: "M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" }))),
                        React.createElement(ShareButton, { "$bgColor": "#16a34a", "$hoverColor": "#15803d", onClick: () => window.open('https://linkedin.com', '_blank') },
                            React.createElement("svg", { width: "20", height: "20", fill: "currentColor", viewBox: "0 0 24 24" },
                                React.createElement("path", { d: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" })))))),
                React.createElement(NewSubmissionLink, { onClick: () => dispatch("startNewSubmission"), "$color": styles.primaryColor }, "Submit another response")))));
}
